import { useState, useRef, useEffect, useCallback } from 'react';
import ImageDrop from '../ImageDrop';
import { authFetch } from '../../auth';
import { useDragReorder } from '../useDragReorder';

let _uid = 0;
const uid = () => `sb${++_uid}`;

const newText     = () => ({ _id: uid(), heading: '', body: '' });
const newCard     = () => ({ _id: uid(), imageKey: '', heading: '', body: '' });
const newSpecRow  = () => ({ _id: uid(), key: '', value: '' });
const newLinkRow  = () => ({ _id: uid(), key: 'Information', url: '' });
const newSpecItem = () => ({ _id: uid(), model: '', category: '', imageKey: '', specs: [newSpecRow()], links: [newLinkRow()] });
const newFileItem = () => ({ _id: uid(), title: '', description: '', url: '' });

function newSection(type) {
  const _id = uid();
  if (type === 'imageSplit') return { _id, type, imageKey: '', imagePos: 'left', texts: [newText()] };
  if (type === 'text')       return { _id, type, heading: '', body: '' };
  if (type === 'bullets')    return { _id, type, heading: '', items: [''] };
  if (type === 'iconGrid')   return { _id, type, cards: [newCard(), newCard()] };
  if (type === 'cardGrid')   return { _id, type, heading: '', cards: [newCard()] };
  if (type === 'specCards')  return { _id, type, layout: 'horizontal', items: [newSpecItem()] };
  if (type === 'files')      return { _id, type, heading: '', items: [newFileItem()] };
  if (type === 'image')      return { _id, type, imageKey: '' };
  if (type === 'table')      return { _id, type, heading: '', cols: ['항목', '내용'], rows: [['', ''], ['', '']] };
  if (type === 'video')      return { _id, type, url: '' };
  return { _id, type };
}

function parse(value) {
  if (!value?.trim()) return [];
  try {
    const o = JSON.parse(value);
    if (o?.__s && Array.isArray(o.__s)) {
      return o.__s.map(s => ({ ...s, _id: s._id || uid() }));
    }
  } catch (_) {}
  return [];
}

function serialize(secs) {
  if (!secs.length) return '';
  return JSON.stringify({ __s: secs.map(({ _id, ...rest }) => rest) });
}

const TYPE_COLOR = { imageSplit: '#3b82f6', text: '#10b981', bullets: '#f59e0b', iconGrid: '#8b5cf6', cardGrid: '#ec4899', specCards: '#0d9488', files: '#7c3aed', image: '#64748b', table: '#0891b2', video: '#ef4444' };
const TYPE_LABEL = { imageSplit: '이미지 분할', text: '텍스트', bullets: '불릿 목록', iconGrid: '아이콘 그리드', cardGrid: '카드 그리드', specCards: '스펙 카드', files: '파일 다운로드', image: '이미지', table: '표', video: '영상' };

const INP = {
  background: 'transparent', border: 'none',
  borderBottom: '1px solid #e2e8f0', outline: 'none',
  width: '100%', fontFamily: 'inherit', fontSize: '0.82rem',
  color: '#0f172a', padding: '4px 0',
};
const BTN_SM = {
  border: 'none', borderRadius: 4, padding: '2px 8px',
  cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
  lineHeight: 1.5, background: '#f1f5f9', color: '#374151',
};

export function SectionBuilder({ value, onChange }) {
  const [secs, setSecs] = useState(() => parse(value));
  const lastEmitted = useRef('');
  const filePick = useRef(null);
  const filePending = useRef(null);
  const [fileUploading, setFileUploading] = useState(false);

  const uploadFile = async (file) => {
    if (!filePending.current) return;
    const applyUrl = filePending.current;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch('/api/files', { method: 'POST', body: fd });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || `업로드 실패 (${res.status})`); }
      const f = await res.json();
      applyUrl(f.url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.message || '파일 업로드 실패');
    } finally {
      setFileUploading(false);
      filePending.current = null;
    }
  };

  useEffect(() => {
    if (value !== lastEmitted.current) {
      setSecs(parse(value));
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = (next) => {
    setSecs(next);
    const json = serialize(next);
    lastEmitted.current = json;
    onChange(json);
  };

  const reorder = useCallback((from, to) => {
    setSecs(prev => {
      const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m);
      const json = serialize(a); lastEmitted.current = json; onChange(json);
      return a;
    });
  }, [onChange]);

  const { containerRef, dragProps, dropProps, cardClass } = useDragReorder(reorder);

  const add  = (type) => emit([...secs, newSection(type)]);
  const del  = (i)    => emit(secs.filter((_, j) => j !== i));
  const up   = (i)    => { if (i === 0) return; const a = [...secs]; [a[i-1], a[i]] = [a[i], a[i-1]]; emit(a); };
  const down = (i)    => { if (i >= secs.length - 1) return; const a = [...secs]; [a[i], a[i+1]] = [a[i+1], a[i]]; emit(a); };
  const upd  = (i, f, v) => emit(secs.map((s, j) => j !== i ? s : { ...s, [f]: v }));

  const updBullet = (si, ii, v) => { const items = [...secs[si].items]; items[ii] = v; upd(si, 'items', items); };
  const addBullet = (si)        => upd(si, 'items', [...secs[si].items, '']);
  const delBullet = (si, ii)    => upd(si, 'items', secs[si].items.filter((_, j) => j !== ii));

  const getTexts = (si) => secs[si].texts || [];
  const updText = (si, ti, field, v) => {
    const texts = getTexts(si).map((t, j) => j !== ti ? t : { ...t, [field]: v });
    upd(si, 'texts', texts);
  };
  const addText = (si) => upd(si, 'texts', [...getTexts(si), newText()]);
  const delText = (si, ti) => upd(si, 'texts', getTexts(si).filter((_, j) => j !== ti));

  const getCards = (si) => secs[si].cards || [];

  // table helpers
  const updCell    = (si, ri, ci, v) => { const rows = secs[si].rows.map((r, i) => i !== ri ? r : r.map((c, j) => j !== ci ? c : v)); upd(si, 'rows', rows); };
  const updCol     = (si, ci, v)     => { const cols = secs[si].cols.map((c, j) => j !== ci ? c : v); upd(si, 'cols', cols); };
  const addRow     = (si)            => { const cols = secs[si].cols || []; upd(si, 'rows', [...secs[si].rows, cols.map(() => '')]); };
  const delRow     = (si, ri)        => upd(si, 'rows', secs[si].rows.filter((_, i) => i !== ri));
  const addCol     = (si)            => emit(secs.map((s, j) => j !== si ? s : { ...s, cols: [...s.cols, `열 ${s.cols.length + 1}`], rows: s.rows.map(r => [...r, '']) }));
  const delCol     = (si, ci)        => emit(secs.map((s, j) => j !== si ? s : { ...s, cols: s.cols.filter((_, k) => k !== ci), rows: s.rows.map(r => r.filter((_, k) => k !== ci)) }));
  const updCard = (si, ci, field, v) => {
    const cards = getCards(si).map((c, j) => j !== ci ? c : { ...c, [field]: v });
    upd(si, 'cards', cards);
  };
  const addCard = (si) => upd(si, 'cards', [...getCards(si), newCard()]);
  const delCard = (si, ci) => upd(si, 'cards', getCards(si).filter((_, j) => j !== ci));

  const getItems  = (si)          => secs[si].items || [];
  const addItem   = (si)          => upd(si, 'items', [...getItems(si), newSpecItem()]);
  const delItem   = (si, ii)      => upd(si, 'items', getItems(si).filter((_, j) => j !== ii));
  const updItem   = (si, ii, f, v) => upd(si, 'items', getItems(si).map((it, j) => j !== ii ? it : { ...it, [f]: v }));
  const updSpec   = (si, ii, spi, f, v) => upd(si, 'items', getItems(si).map((it, j) => j !== ii ? it : { ...it, specs: (it.specs || []).map((sp, k) => k !== spi ? sp : { ...sp, [f]: v }) }));
  const addSpec   = (si, ii)      => upd(si, 'items', getItems(si).map((it, j) => j !== ii ? it : { ...it, specs: [...(it.specs || []), newSpecRow()] }));
  const delSpec   = (si, ii, spi) => upd(si, 'items', getItems(si).map((it, j) => j !== ii ? it : { ...it, specs: (it.specs || []).filter((_, k) => k !== spi) }));
  const updLink   = (si, ii, li, f, v) => upd(si, 'items', getItems(si).map((it, j) => j !== ii ? it : { ...it, links: (it.links || []).map((lnk, k) => k !== li ? lnk : { ...lnk, [f]: v }) }));
  const addLink   = (si, ii)      => upd(si, 'items', getItems(si).map((it, j) => j !== ii ? it : { ...it, links: [...(it.links || []), newLinkRow()] }));
  const delLink   = (si, ii, li)  => upd(si, 'items', getItems(si).map((it, j) => j !== ii ? it : { ...it, links: (it.links || []).filter((_, k) => k !== li) }));

  // files section helpers
  const getFileItems = (si) => secs[si].items || [];
  const updFileItem  = (si, fi, f, v) => upd(si, 'items', getFileItems(si).map((it, j) => j !== fi ? it : { ...it, [f]: v }));
  const addFileItem  = (si) => upd(si, 'items', [...getFileItems(si), newFileItem()]);
  const delFileItem  = (si, fi) => upd(si, 'items', getFileItems(si).filter((_, j) => j !== fi));

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 0' }}>
      {secs.length === 0 && (
        <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem', border: '1.5px dashed #e2e8f0', borderRadius: 8 }}>
          아래 버튼으로 섹션을 추가하세요
        </div>
      )}

      {secs.map((s, i) => (
        <div key={s._id} data-flip={s._id}
          className={cardClass(i)}
          style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}
          {...dragProps(i)} {...dropProps(i)}>
          {/* 카드 헤더 */}
          <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#bbb', fontSize: '1rem', cursor: 'grab', padding: '0 4px', userSelect: 'none', lineHeight: 1, flexShrink: 0 }}
              title="드래그하여 순서 변경">⠿</span>
            <span style={{ background: TYPE_COLOR[s.type] || '#6b7280', color: '#fff', fontSize: '0.63rem', fontWeight: 800, padding: '2px 7px', borderRadius: 4 }}>
              {TYPE_LABEL[s.type] || s.type}
            </span>
            <span style={{ fontSize: '0.78rem', color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.type === 'imageSplit' ? (s.texts?.[0]?.heading || '(제목 없음)') : (s.heading || '(제목 없음)')}
            </span>
            <button type="button" onClick={() => up(i)}   style={{ ...BTN_SM, opacity: i === 0 ? 0.3 : 1 }}>▲</button>
            <button type="button" onClick={() => down(i)} style={{ ...BTN_SM, opacity: i >= secs.length - 1 ? 0.3 : 1 }}>▼</button>
            <button type="button" onClick={() => del(i)}  style={{ ...BTN_SM, background: '#fee2e2', color: '#dc2626' }}>✕</button>
          </div>

          {/* 카드 본문 */}
          <div style={{ padding: 14 }}>
            {s.type === 'imageSplit' && (
              <>
                <div style={{ display: 'flex', gap: 14 }}>
                  {/* 이미지 */}
                  <div style={{ width: 180, flexShrink: 0 }}>
                    <ImageDrop value={s.imageKey} onChange={(k) => upd(i, 'imageKey', k)} />
                    <div style={{ display: 'flex', gap: 12, fontSize: '0.72rem', color: '#6b7280', marginTop: 8 }}>
                      <span style={{ fontWeight: 700 }}>위치:</span>
                      {[['left', '왼쪽'], ['right', '오른쪽']].map(([v, l]) => (
                        <label key={v} style={{ display: 'flex', gap: 3, cursor: 'pointer', alignItems: 'center' }}>
                          <input type="radio" checked={s.imagePos === v} onChange={() => upd(i, 'imagePos', v)} />{l}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 제목+본문 목록 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(s.texts || []).map((t, ti) => (
                      <div key={t._id || ti} style={{ border: '1px solid #e2e8f0', borderRadius: 7, padding: '10px 12px', position: 'relative' }}>
                        {(s.texts || []).length > 1 && (
                          <button type="button" onClick={() => delText(i, ti)}
                            style={{ ...BTN_SM, position: 'absolute', top: 6, right: 6, background: '#fee2e2', color: '#dc2626', padding: '1px 6px' }}>✕</button>
                        )}
                        <input style={{ ...INP, fontWeight: 600, marginBottom: 6 }}
                          value={t.heading} placeholder="제목"
                          onChange={(e) => updText(i, ti, 'heading', e.target.value)} />
                        <textarea style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '6px 8px', resize: 'vertical', minHeight: 70, lineHeight: 1.6, fontSize: '0.82rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
                          value={t.body} placeholder="본문"
                          onChange={(e) => updText(i, ti, 'body', e.target.value)} />
                      </div>
                    ))}
                    <button type="button" onClick={() => addText(i)}
                      style={{ ...BTN_SM, alignSelf: 'flex-start' }}>+ 제목/본문 추가</button>
                  </div>
                </div>
              </>
            )}

            {s.type === 'text' && (
              <>
                <input style={{ ...INP, fontWeight: 600, marginBottom: 8 }}
                  value={s.heading} placeholder="제목 (선택 입력)"
                  onChange={(e) => upd(i, 'heading', e.target.value)} />
                <textarea style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px', resize: 'vertical', minHeight: 100, lineHeight: 1.7, fontSize: '0.82rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  value={s.body} placeholder="본문 내용" rows={5}
                  onChange={(e) => upd(i, 'body', e.target.value)} />
              </>
            )}

            {s.type === 'bullets' && (
              <>
                <input style={{ ...INP, fontWeight: 600, marginBottom: 10 }}
                  value={s.heading} placeholder="제목 (선택 입력)"
                  onChange={(e) => upd(i, 'heading', e.target.value)} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                  {s.items.map((item, ii) => (
                    <div key={ii} style={{ display: 'flex', gap: 6, alignItems: 'center', background: '#f8fafc', borderRadius: 6, padding: '3px 6px' }}>
                      <span style={{ color: '#9ca3af', fontWeight: 700, width: 14, flexShrink: 0 }}>•</span>
                      <input style={{ ...INP, flex: 1 }} value={item} placeholder={`항목 ${ii + 1}`}
                        onChange={(e) => updBullet(i, ii, e.target.value)} />
                      <button type="button" onClick={() => delBullet(i, ii)}
                        style={{ ...BTN_SM, background: '#fee2e2', color: '#dc2626', padding: '2px 6px' }}>✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addBullet(i)}
                    style={{ ...BTN_SM, gridColumn: '1 / -1', alignSelf: 'flex-start', marginTop: 4 }}>+ 항목 추가</button>
                </div>
              </>
            )}

            {s.type === 'image' && (
              <ImageDrop value={s.imageKey} onChange={(k) => upd(i, 'imageKey', k)} />
            )}

            {s.type === 'table' && (
              <>
                <input style={{ ...INP, fontWeight: 600, marginBottom: 10 }}
                  value={s.heading} placeholder="표 제목 (선택 입력)"
                  onChange={(e) => upd(i, 'heading', e.target.value)} />
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        {(s.cols || []).map((col, ci) => (
                          <th key={ci} style={{ border: '1px solid #e2e8f0', background: '#f1f5f9', padding: 0, minWidth: 100, position: 'relative' }}>
                            <input
                              value={col}
                              onChange={(e) => updCol(i, ci, e.target.value)}
                              style={{ width: '100%', border: 'none', background: 'transparent', fontWeight: 700, padding: '6px 24px 6px 8px', outline: 'none', fontSize: '0.8rem', boxSizing: 'border-box' }} />
                            {(s.cols || []).length > 1 && (
                              <button type="button" onClick={() => delCol(i, ci)}
                                style={{ position: 'absolute', top: 2, right: 2, ...BTN_SM, padding: '0px 4px', background: '#fee2e2', color: '#dc2626', fontSize: '0.62rem' }}>✕</button>
                            )}
                          </th>
                        ))}
                        <th style={{ border: '1px solid #e2e8f0', background: '#f8fafc', width: 36, padding: 0 }}>
                          <button type="button" onClick={() => addCol(i)}
                            style={{ ...BTN_SM, width: '100%', padding: '6px 0', background: 'transparent', fontSize: '0.75rem' }}>+열</button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(s.rows || []).map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci} style={{ border: '1px solid #e2e8f0', padding: 0 }}>
                              <input
                                value={cell}
                                onChange={(e) => updCell(i, ri, ci, e.target.value)}
                                style={{ width: '100%', border: 'none', padding: '6px 8px', outline: 'none', fontSize: '0.8rem', boxSizing: 'border-box' }} />
                            </td>
                          ))}
                          <td style={{ border: '1px solid #e2e8f0', textAlign: 'center', padding: 0 }}>
                            {(s.rows || []).length > 1 && (
                              <button type="button" onClick={() => delRow(i, ri)}
                                style={{ ...BTN_SM, padding: '2px 6px', background: '#fee2e2', color: '#dc2626', fontSize: '0.65rem' }}>✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" onClick={() => addRow(i)}
                  style={{ ...BTN_SM, alignSelf: 'flex-start', marginTop: 6 }}>+ 행 추가</button>
              </>
            )}

            {s.type === 'video' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 700 }}>영상 URL (YouTube 또는 직접 링크)</label>
                <input style={{ ...INP, borderBottom: '1px solid #e2e8f0' }}
                  value={s.url} placeholder="https://www.youtube.com/watch?v=..."
                  onChange={(e) => upd(i, 'url', e.target.value)} />
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '0.72rem', color: '#3b82f6', marginTop: 2 }}>
                    링크 확인 →
                  </a>
                )}
              </div>
            )}

            {s.type === 'iconGrid' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {getCards(i).map((c, ci) => (
                    <div key={c._id || ci} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 10px 8px', position: 'relative', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {getCards(i).length > 1 && (
                        <button type="button" onClick={() => delCard(i, ci)}
                          style={{ ...BTN_SM, position: 'absolute', top: 4, right: 4, background: '#fee2e2', color: '#dc2626', padding: '1px 5px', fontSize: '0.65rem' }}>✕</button>
                      )}
                      <div style={{ width: '100%', maxWidth: 80, margin: '0 auto' }}>
                        <ImageDrop value={c.imageKey} onChange={(k) => updCard(i, ci, 'imageKey', k)} />
                      </div>
                      <input style={{ ...INP, fontWeight: 700, textAlign: 'center', fontSize: '0.8rem' }}
                        value={c.heading} placeholder="제목"
                        onChange={(e) => updCard(i, ci, 'heading', e.target.value)} />
                      <textarea style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '5px 7px', resize: 'vertical', minHeight: 60, lineHeight: 1.5, fontSize: '0.75rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
                        value={c.body} placeholder="본문"
                        onChange={(e) => updCard(i, ci, 'body', e.target.value)} />
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => addCard(i)}
                  style={{ ...BTN_SM, alignSelf: 'flex-start' }}>+ 카드 추가</button>
              </div>
            )}

            {s.type === 'cardGrid' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input style={{ ...INP, fontWeight: 600, marginBottom: 4 }}
                  value={s.heading || ''} placeholder="섹션 제목 (선택 입력)"
                  onChange={(e) => upd(i, 'heading', e.target.value)} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {getCards(i).slice(0, 3).map((c, ci) => (
                    <div key={c._id || ci} style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                      {getCards(i).length > 1 && (
                        <button type="button" onClick={() => delCard(i, ci)}
                          style={{ ...BTN_SM, position: 'absolute', top: 4, right: 4, background: '#fee2e2', color: '#dc2626', padding: '1px 5px', fontSize: '0.65rem', zIndex: 1 }}>✕</button>
                      )}
                      <ImageDrop value={c.imageKey} onChange={(k) => updCard(i, ci, 'imageKey', k)} />
                      <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <input style={{ ...INP, fontWeight: 700, fontSize: '0.82rem' }}
                          value={c.heading} placeholder="카드 제목"
                          onChange={(e) => updCard(i, ci, 'heading', e.target.value)} />
                        <textarea style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '5px 7px', resize: 'vertical', minHeight: 60, lineHeight: 1.5, fontSize: '0.75rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
                          value={c.body} placeholder="설명"
                          onChange={(e) => updCard(i, ci, 'body', e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>
                {getCards(i).length < 3 && (
                  <button type="button" onClick={() => addCard(i)}
                    style={{ ...BTN_SM, alignSelf: 'flex-start' }}>+ 카드 추가 ({getCards(i).length}/3)</button>
                )}
              </div>
            )}

            {s.type === 'specCards' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>레이아웃:</span>
                  {[['horizontal', '가로형'], ['vertical', '세로형']].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => upd(i, 'layout', val)}
                      style={{ ...BTN_SM, background: (s.layout || 'horizontal') === val ? '#0d9488' : '#f1f5f9', color: (s.layout || 'horizontal') === val ? '#fff' : '#374151', border: 'none' }}>
                      {label}
                    </button>
                  ))}
                </div>
                {getItems(i).map((item, ii) => (
                  <div key={item._id || ii} style={{ border: '1px solid #ccfbf1', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ background: '#f0fdfa', borderBottom: '1px solid #ccfbf1', padding: '5px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.72rem', color: '#0d9488', fontWeight: 700 }}>제품 {ii + 1}</span>
                      {getItems(i).length > 1 && (
                        <button type="button" onClick={() => delItem(i, ii)}
                          style={{ ...BTN_SM, background: '#fee2e2', color: '#dc2626', padding: '1px 6px', fontSize: '0.65rem' }}>✕ 삭제</button>
                      )}
                    </div>
                    <div style={{ padding: '10px 12px', display: 'flex', gap: 12 }}>
                      <div style={{ width: 110, flexShrink: 0 }}>
                        <ImageDrop value={item.imageKey} onChange={(k) => updItem(i, ii, 'imageKey', k)} />
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <input style={{ ...INP, fontWeight: 700 }}
                          value={item.model} placeholder="모델명"
                          onChange={(e) => updItem(i, ii, 'model', e.target.value)} />
                        <input style={INP}
                          value={item.category} placeholder="카테고리 (선택)"
                          onChange={(e) => updItem(i, ii, 'category', e.target.value)} />
                        <div style={{ marginTop: 6 }}>
                          <div style={{ fontSize: '0.68rem', color: '#6b7280', fontWeight: 700, marginBottom: 3 }}>스펙</div>
                          {(item.specs || []).map((sp, spi) => (
                            <div key={sp._id || spi} style={{ display: 'flex', gap: 4, marginBottom: 3, alignItems: 'flex-start' }}>
                              <input style={{ ...INP, width: '36%', fontWeight: 600 }}
                                value={sp.key} placeholder="항목명"
                                onChange={(e) => updSpec(i, ii, spi, 'key', e.target.value)} />
                              <textarea style={{ ...INP, flex: 1, resize: 'vertical', minHeight: 26, lineHeight: '1.4', verticalAlign: 'top' }}
                                value={sp.value} placeholder="내용 (Enter로 줄나눔)"
                                rows={1}
                                onChange={(e) => updSpec(i, ii, spi, 'value', e.target.value)} />
                              {(item.specs || []).length > 1 && (
                                <button type="button" onClick={() => delSpec(i, ii, spi)}
                                  style={{ ...BTN_SM, background: '#fee2e2', color: '#dc2626', padding: '1px 5px', fontSize: '0.62rem', flexShrink: 0, marginTop: 2 }}>✕</button>
                              )}
                            </div>
                          ))}
                          <button type="button" onClick={() => addSpec(i, ii)}
                            style={{ ...BTN_SM, fontSize: '0.68rem', marginTop: 2 }}>+ 스펙 추가</button>
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <div style={{ fontSize: '0.68rem', color: '#6b7280', fontWeight: 700, marginBottom: 3 }}>링크 버튼</div>
                          {(item.links || []).map((lnk, li) => (
                            <div key={lnk._id || li} style={{ display: 'flex', gap: 4, marginBottom: 3, alignItems: 'center' }}>
                              <input style={{ ...INP, width: '28%' }}
                                value={lnk.key} placeholder="버튼명"
                                onChange={(e) => updLink(i, ii, li, 'key', e.target.value)} />
                              <input style={{ ...INP, flex: 1 }}
                                value={lnk.url} placeholder="https:// 또는 /files/up/..."
                                onChange={(e) => updLink(i, ii, li, 'url', e.target.value)} />
                              <button type="button" onClick={() => delLink(i, ii, li)}
                                style={{ ...BTN_SM, background: '#fee2e2', color: '#dc2626', padding: '1px 5px', fontSize: '0.62rem', flexShrink: 0 }}>✕</button>
                            </div>
                          ))}
                          <div style={{ display: 'flex', gap: 5, marginTop: 2, flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => addLink(i, ii)}
                              style={{ ...BTN_SM, fontSize: '0.68rem' }}>+ 링크 추가</button>
                            <button type="button" disabled={fileUploading}
                              onClick={() => {
                                filePending.current = (url) => setSecs(prev => {
                                  const next = prev.map((s, j) => j !== i ? s : {
                                    ...s,
                                    items: (s.items || []).map((it, k) => k !== ii ? it : {
                                      ...it,
                                      links: [...(it.links || []), { _id: uid(), key: 'Information', url }],
                                    }),
                                  });
                                  const json = serialize(next);
                                  lastEmitted.current = json;
                                  onChange(json);
                                  return next;
                                });
                                filePick.current?.click();
                              }}
                              style={{ ...BTN_SM, fontSize: '0.68rem', background: '#eff6ff', color: '#3b82f6' }}>
                              {fileUploading ? '업로드 중…' : '+ 파일 추가'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => addItem(i)}
                  style={{ ...BTN_SM, alignSelf: 'flex-start' }}>+ 제품 추가</button>
              </div>
            )}

            {s.type === 'files' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input style={{ ...INP, fontWeight: 600, marginBottom: 4 }}
                  value={s.heading || ''} placeholder="섹션 제목 (선택 입력)"
                  onChange={(e) => upd(i, 'heading', e.target.value)} />
                {getFileItems(i).map((fit, fi) => (
                  <div key={fit._id || fi} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', border: '1px solid #e9d5ff', borderRadius: 7, padding: '8px 10px', background: '#faf5ff' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <input style={{ ...INP, fontWeight: 600 }}
                        value={fit.title} placeholder="파일 표시명 (예: 제품 카탈로그)"
                        onChange={(e) => updFileItem(i, fi, 'title', e.target.value)} />
                      <input style={INP}
                        value={fit.description || ''} placeholder="설명 (선택 입력)"
                        onChange={(e) => updFileItem(i, fi, 'description', e.target.value)} />
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input style={{ ...INP, flex: 1, fontSize: '0.75rem', color: fit.url ? '#7c3aed' : undefined }}
                          value={fit.url} placeholder="URL 또는 아래 📎로 파일 업로드"
                          onChange={(e) => updFileItem(i, fi, 'url', e.target.value)} />
                        <button type="button"
                          title="PDF/문서 파일 업로드"
                          disabled={fileUploading}
                          onClick={() => {
                            filePending.current = (url) => setSecs(prev => {
                              const next = prev.map((s2, j) => j !== i ? s2 : {
                                ...s2,
                                items: (s2.items || []).map((it, k) => k !== fi ? it : { ...it, url }),
                              });
                              const json = serialize(next);
                              lastEmitted.current = json;
                              onChange(json);
                              return next;
                            });
                            filePick.current?.click();
                          }}
                          style={{ ...BTN_SM, background: '#ede9fe', color: '#7c3aed', padding: '2px 8px', fontSize: '0.7rem', flexShrink: 0 }}>
                          {fileUploading ? '…' : '📎 파일'}
                        </button>
                      </div>
                    </div>
                    <button type="button" onClick={() => delFileItem(i, fi)}
                      style={{ ...BTN_SM, background: '#fee2e2', color: '#dc2626', padding: '2px 6px', fontSize: '0.65rem', flexShrink: 0, marginTop: 2 }}>✕</button>
                  </div>
                ))}
                <button type="button" onClick={() => addFileItem(i)}
                  style={{ ...BTN_SM, alignSelf: 'flex-start' }}>+ 파일 추가</button>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* 섹션 추가 버튼 */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px 16px', borderRadius: 8, background: '#f3f4f6' }}>
        <span style={{ fontSize: '1rem', color: '#111', alignSelf: 'center', fontWeight: 700, marginRight: 2 }}>섹션 추가:</span>
        {[['imageSplit', '🖼 이미지 분할'], ['iconGrid', '⊞ 아이콘 그리드'], ['cardGrid', '🃏 카드 그리드'], ['specCards', '📦 스펙 카드'], ['files', '📁 파일 다운로드'], ['image', '🏞 이미지'], ['video', '▶ 영상'], ['text', '📄 텍스트'], ['bullets', '📋 불릿 목록'], ['table', '📊 표']].map(([type, label]) => (
          <button key={type} type="button" onClick={() => add(type)}
            style={{ fontSize: '0.75rem', background: TYPE_COLOR[type] + '18', border: `1px solid ${TYPE_COLOR[type]}55`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', color: TYPE_COLOR[type], fontWeight: 600 }}>
            {label}
          </button>
        ))}
      </div>

      {/* 파일 업로드용 숨긴 input (스펙카드 링크 버튼 📎 전용) */}
      <input
        ref={filePick}
        type="file"
        accept=".pdf,.xlsx,.xls,.docx,.doc,.zip"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) uploadFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}
